import React, { useState } from 'react'
import {
  Masonry,
  SERVICES_URL,
  useLocale,
  useTranslation
} from '@plandi/common'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import StarIcon from '@material-ui/icons/Star'
import StarBorderIcon from '@material-ui/icons/StarBorder'
import { useRouter } from 'next/router'
import Link from 'next/link'

import { EmptyContent, URLS } from 'src/common'
import { useProjectDetailsStyles } from './project-details.styles'
import MaterialLink from '@material-ui/core/Link'
import {
  ProjectCard,
  ProjectView,
  useMutationAddFavorite,
  useMutationDeleteFavorite,
  useQueryProjectById
} from '../common'
import { BreadCrumbs } from 'src/features/breadcrumbs/breadcrumbs'
import { ReactComponent as PersonIcon } from '../../../assets/personicon.svg'
import Image from 'next/image'

export const ProjectDetails: React.FC = () => {
  const [isHidden, setHidden] = useState(true)
  const classes = useProjectDetailsStyles()
  const router = useRouter()
  const locale = useLocale()
  const { data: translate } = useTranslation()

  const projectId = router.query.projectId
    ? Number(router.query.projectId)
    : undefined

  const { data: project, isError, isLoading } = useQueryProjectById(projectId)

  const mutationAddFavorite = useMutationAddFavorite(translate)
  const mutationDeleteFavorite = useMutationDeleteFavorite(translate)

  const hasSimilarProjects =
    project?.similarProjects && project?.similarProjects.length > 0

  const handleChangeRouteProjectsUser = () => {
    router.push({
      pathname:
        project?.type === 'user' ? URLS.projects.user : URLS.projects.profile,
      query: {
        userId:
          project?.type === 'user' ? project?.author?.id : project?.profile?.id,
        type: project?.type
      }
    })
  }

  const renderUserNameAndImg = () => {
    return (
      <div className={classes.nameBlock}>
        {(project?.type === 'user' && !project?.author?.avatarUrl) ||
        ((project?.type === 'freelancer' || project?.type === 'company') &&
          !project?.profile?.avatarUrl) ? (
          <PersonIcon className={classes.iconName} />
        ) : (
          <Image
            width={45}
            height={45}
            src={
              (project?.type === 'user'
                ? project.author?.avatarUrl
                : project?.profile?.avatarUrl) as string
            }
            alt=""
            className={classes.avatarUrl}
          />
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginLeft: '12px'
          }}
        >
          <Typography
            variant="body1"
            noWrap
            style={{ color: '#212121' }}
            className={classes.name}
            onClick={handleChangeRouteProjectsUser}
            component={MaterialLink}
            underline="hover"
          >
            {project?.type === 'user'
              ? project?.author?.name
              : project?.type === 'freelancer'
              ? project.profile?.name
              : project?.profile?.name}
          </Typography>
          {project?.type === 'user' ? (
            <Typography variant="caption">
              {translate?.['blog-site']?.['private-person']}
            </Typography>
          ) : project?.type === 'freelancer' ? (
            <Typography variant="caption">{translate?.freelancer}</Typography>
          ) : (
            <Typography variant="caption">{translate?.company}</Typography>
          )}
        </div>
      </div>
    )
  }

  const handleDeleteFavorite = () => {
    if (projectId) {
      mutationDeleteFavorite.mutate(projectId)
    }
  }

  const handleAddFavorite = () => {
    if (projectId) {
      mutationAddFavorite.mutate(projectId)
    }
  }

  const renderFavoriteButton = () => {
    if (project?.isAuthor) {
      return null
    }

    if (project?.isFavorite) {
      return (
        <Button
          startIcon={<StarIcon />}
          variant="contained"
          color="secondary"
          onClick={handleDeleteFavorite}
        >
          {translate?.['delete-btn-favorite']}
        </Button>
      )
    }

    return (
      <Button
        startIcon={<StarBorderIcon />}
        variant="contained"
        color="secondary"
        onClick={handleAddFavorite}
      >
        {translate?.['add-btn-favorite']}
      </Button>
    )
  }

  if (isLoading) {
    return null
  }

  if (isError) {
    return (
      <EmptyContent
        message={translate?.shop['project-not-found-blocked'] ?? ''}
        showButton
        buttonProps={{
          href: URLS.projects.list,
          text: translate?.['go-to-main'] ?? ''
        }}
      />
    )
  }

  const url = SERVICES_URL

  return (
    <>
      <BreadCrumbs
        breadcrumbInfo={[
          {
            title: translate?.market.title ?? '',
            href: locale === 'ru' ? `${url.shop}/ru` : url.shop
          },
          {
            title: translate?.projects ?? '',
            href:
              locale === 'ru'
                ? `${url.shop}/ru/projects`
                : `${url.shop}/projects`
          },
          {
            title: `${project?.name}`,
            href:
              locale === 'ru'
                ? `${url.shop}/ru/projects`
                : `${url.shop}/projects`
          }
        ]}
      />
      <ProjectView
        {...project}
        translate={translate}
        locale={locale}
        renderUserNameAndImg={renderUserNameAndImg}
      />
      <div className={classes.buttonGroup}>
        {renderFavoriteButton()}
        <Link
          href={{
            pathname:
              project?.type === 'user'
                ? URLS.projects.user
                : URLS.projects.profile,
            query: {
              userId:
                project?.type === 'user'
                  ? project?.author?.id
                  : project?.profile?.id,
              type: project?.type
            }
          }}
          passHref
        >
          <Button variant="contained" color="primary">
            {translate?.shop?.['project-page']?.['more-projects']}
          </Button>
        </Link>
      </div>
      {hasSimilarProjects && (
        <div className={classes.similarCompanies}>
          <Typography variant="h4" style={{ marginBottom: 50 }}>
            {translate?.shop['project-page']['similar-projects']}
          </Typography>
          <Masonry>
            {project?.similarProjects?.map((project) => (
              <ProjectCard
                key={project.id}
                {...project}
                translate={translate}
                locale={locale}
              />
            ))}
          </Masonry>
        </div>
      )}
      {project?.seoText?.length && (
        <div className={classes.seoBlock}>
          <p className={classes.seoTitle}>
            {locale === 'ru' ? 'Дополнительно' : 'Additionally'}
          </p>
          <p className={classes.seoDescription}>
            {project?.seoText.length > 207 && isHidden
              ? project?.seoText.slice(0, 207).trim() + '...'
              : project?.seoText}
          </p>
          {project?.seoText.length > 207 && (
            <div style={{ alignSelf: 'flex-end' }}>
              <button
                onClick={() => setHidden((prevState) => !prevState)}
                className={classes.readMoreButton}
              >
                {isHidden
                  ? translate?.shop?.['read-more'] ?? 'Читать далее'
                  : translate?.shop?.collapse ?? 'Свернуть'}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  )
}
